// Copyright 2019 Sorama B.V.

import { Quad } from "datatypes/Generic/Quad";
import * as numeric from "numericjs";

export class AffineTransformFinder {
    /**
     * Find the 4x4 affine transform from quad a to quad b. This transform may be used in the css matrix3d transform.
     * @param a Quad to transform from
     * @param b Quad to transform to
     */
    public static findTransform4(a: Quad, b: Quad): number[][] {
        const h = AffineTransformFinder.findTransform3(a, b);
        return [
            [h[0][0], h[0][1], 0, h[0][2]],
            [h[1][0], h[1][1], 0, h[1][2]],
            [0, 0, 1, 0],
            [h[2][0], h[2][1], 0, h[2][2]],
        ];
    }

    /**
     * Find the 3x3 affine transform from quad a to quad b. Use findTransform4 for css matrix3d transforms.
     * @param a Quad to transform from
     * @param b Quad to transform to
     */
    public static findTransform3(a: Quad, b: Quad): number[][] {
        // Find a transform that maps this quad to the unit quad
        // See https://math.stackexchange.com/a/2619023
        // Step 1: Create problem matrix
        const x0 = a.topLeft.x;
        const x1 = a.topRight.x;
        const x2 = a.bottomRight.x;
        const x3 = a.bottomLeft.x;

        const y0 = a.topLeft.y;
        const y1 = a.topRight.y;
        const y2 = a.bottomRight.y;
        const y3 = a.bottomLeft.y;

        const u0 = b.topLeft.x;
        const u1 = b.topRight.x;
        const u2 = b.bottomRight.x;
        const u3 = b.bottomLeft.x;

        const v0 = b.topLeft.y;
        const v1 = b.topRight.y;
        const v2 = b.bottomRight.y;
        const v3 = b.bottomLeft.y;

        const A = [
            [-x0, -y0, -1, 0, 0, 0, x0 * u0, y0 * u0, u0],
            [0, 0, 0, -x0, -y0, -1, x0 * v0, y0 * v0, v0],
            [-x1, -y1, -1, 0, 0, 0, x1 * u1, y1 * u1, u1],
            [0, 0, 0, -x1, -y1, -1, x1 * v1, y1 * v1, v1],
            [-x2, -y2, -1, 0, 0, 0, x2 * u2, y2 * u2, u2],
            [0, 0, 0, -x2, -y2, -1, x2 * v2, y2 * v2, v2],
            [-x3, -y3, -1, 0, 0, 0, x3 * u3, y3 * u3, u3],
            [0, 0, 0, -x3, -y3, -1, x3 * v3, y3 * v3, v3],
            [0, 0, 0, 0, 0, 0, 0, 0, 1],
        ];

        const h = numeric.dot(AffineTransformFinder.pinv(A), [[0], [0], [0], [0], [0], [0], [0], [0], [1]]);
        return [
            [h[0][0], h[1][0], h[2][0]],
            [h[3][0], h[4][0], h[5][0]],
            [h[6][0], h[7][0], h[8][0]],
        ];
    }

    public static toCSSTransform(matrix: number[][]): string {
        if (!matrix) {
            return "";
        } else {
            return (
                "matrix3d(" +
                `${matrix[0][0]},` +
                `${matrix[1][0]},` +
                `${matrix[2][0]},` +
                `${matrix[3][0]},` +
                `${matrix[0][1]},` +
                `${matrix[1][1]},` +
                `${matrix[2][1]},` +
                `${matrix[3][1]},` +
                `${matrix[0][2]},` +
                `${matrix[1][2]},` +
                `${matrix[2][2]},` +
                `${matrix[3][2]},` +
                `${matrix[0][3]},` +
                `${matrix[1][3]},` +
                `${matrix[2][3]},` +
                `${matrix[3][3]}` +
                ")"
            );
        }
    }

    private static pinv(A: number[][]): number[][] {
        return numeric.dot(numeric.inv(numeric.dot(numeric.transpose(A), A)), numeric.transpose(A));
    }
}
